import React, { useCallback, useEffect, useState } from "react";
import { ShieldCheck, UserRound } from "lucide-react";
import {
  Account,
  accountApi,
  accountErr,
  SecurityInfo,
} from "../../api/account/accountApi";
import ProfileSection from "./ProfileSection";
import ContactSection from "./ContactSection";
import PasswordSection from "./PasswordSection";
import TwoFactorSection from "./TwoFactorSection";
import "./account.scss";

type Tab = "profile" | "security";

/**
 * "My Account": everything a signed-in user can change about themselves.
 * Data comes from authz-api /auth/me/* which proxies Zitadel's self-service
 * API with the user's own token — no admin rights involved.
 */
const AccountPage: React.FC = () => {
  const [tab, setTab] = useState<Tab>("profile");
  const [account, setAccount] = useState<Account | null>(null);
  const [security, setSecurity] = useState<SecurityInfo | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const loadSecurity = useCallback(async () => {
    try {
      const r = await accountApi.security();
      setSecurity(r.data);
    } catch (e) {
      setError(accountErr(e, "Could not load security settings"));
    }
  }, []);

  // Security keys are enrolled in the Zitadel console (another tab); pick
  // the new key up as soon as the user comes back.
  useEffect(() => {
    const onFocus = () => {
      if (document.visibilityState === "visible") loadSecurity();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [loadSecurity]);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const [acc, sec] = await Promise.all([accountApi.get(), accountApi.security()]);
        if (!alive) return;
        setAccount(acc.data);
        setSecurity(sec.data);
      } catch (e) {
        if (alive) setError(accountErr(e, "Could not load your account"));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  return (
    <div className="account">
      <h2 className="account__title">My Account</h2>
      <p className="account__subtitle">
        {account ? account.login_names[0] || account.username : "Manage your profile and sign-in security."}
      </p>

      <div className="account__tabs">
        <button className={tab === "profile" ? "active" : ""} onClick={() => setTab("profile")}>
          <UserRound size={16} /> Profile
        </button>
        <button className={tab === "security" ? "active" : ""} onClick={() => setTab("security")}>
          <ShieldCheck size={16} /> Security
        </button>
      </div>

      {error && (
        <div className="acct-notice acct-notice--error" style={{ marginBottom: 16 }}>
          {error}
        </div>
      )}

      {loading ? (
        <p className="acct-loading">Loading your account...</p>
      ) : !account ? (
        !error && <p className="acct-empty">Account details are unavailable right now.</p>
      ) : tab === "profile" ? (
        <div className="account__grid">
          <ProfileSection account={account} onSaved={setAccount} />
          <ContactSection account={account} onChanged={setAccount} />
        </div>
      ) : (
        <div className="account__grid">
          {security && (
            <TwoFactorSection
              account={account}
              security={security}
              onChanged={loadSecurity}
              onGoToContact={() => setTab("profile")}
            />
          )}
          <PasswordSection />
        </div>
      )}
    </div>
  );
};

export default AccountPage;
